import { useCallback } from 'react';
import * as Cesium from 'cesium';
import type { MapTheme } from '../types';
import { buildMapThemeProvider } from '../utils/mapThemeProvider';

export function useSecondaryViewer(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  clockSyncRemoveRef: React.MutableRefObject<Cesium.Event.RemoveCallback | null>,
  cameraSyncRemoveRef: React.MutableRefObject<Cesium.Event.RemoveCallback | null>,
  secondarySharedDataSourceRef: React.MutableRefObject<any>,
  secondaryHighlightDataSourceRef: React.MutableRefObject<any>,
  secondarySelectionDataSourceRef: React.MutableRefObject<any>,
  secondaryPreviewDataSourceRef: React.MutableRefObject<any>,
  secondaryMeasureDataSourceRef: React.MutableRefObject<any>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  highlightDataSourceRef: React.MutableRefObject<any>,
  selectionDataSourceRef: React.MutableRefObject<any>,
  previewDataSourceRef: React.MutableRefObject<any>,
  measureDataSourceRef: React.MutableRefObject<any>,
  mapTheme: MapTheme,
) {
  const initSecondaryViewer = useCallback((el: HTMLDivElement) => {
    if (secondaryViewerRef.current && !secondaryViewerRef.current.isDestroyed()) {
      const sv = secondaryViewerRef.current;
      sv.useDefaultRenderLoop = true;
      if (!clockSyncRemoveRef.current) {
        const primary = viewerRef.current;
        if (primary) {
          clockSyncRemoveRef.current = primary.clock.onTick.addEventListener((tick: Cesium.Clock) => {
            if (!secondaryViewerRef.current || secondaryViewerRef.current.isDestroyed()) return;
            secondaryViewerRef.current.clock.currentTime = tick.currentTime.clone();
          });
        }
      }
      if (!cameraSyncRemoveRef.current) {
        const primary = viewerRef.current;
        if (primary) {
          cameraSyncRemoveRef.current = primary.camera.changed.addEventListener(() => {
            const sv2 = secondaryViewerRef.current;
            if (!sv2 || sv2.isDestroyed()) return;
            try {
              const cart = Cesium.Cartographic.fromCartesian(primary.camera.positionWC);
              if (cart) {
                sv2.camera.setView({
                  destination: Cesium.Cartesian3.fromRadians(cart.longitude, cart.latitude, Math.max(cart.height, 2_000_000)),
                });
              }
            } catch { /* ignore */ }
          });
        }
      }
      return;
    }

    const sv = new Cesium.Viewer(el, {
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      animation: false,
      timeline: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      shadows: false,
      shouldAnimate: false,
      orderIndependentTranslucency: false,
      maximumRenderTimeChange: 0.0,
      targetFrameRate: 60,
      contextOptions: {
        webgl: {
          alpha: false,
          depth: true,
          stencil: false,
          antialias: false,
          powerPreference: 'high-performance',
        },
        allowTextureFilterAnisotropic: false,
      },
    });

    sv.useBrowserRecommendedResolution = false;
    sv.resolutionScale = 1.0;
    sv.scene.globe.depthTestAgainstTerrain = true;
    sv.scene.globe.enableLighting = false;
    sv.scene.globe.maximumScreenSpaceError = 2.0;
    sv.scene.globe.tileCacheSize = 100;
    sv.scene.globe.loadingDescendantLimit = 2;
    sv.scene.screenSpaceCameraController.enableCollisionDetection = false;

    if (mapTheme !== 'default') {
      void buildMapThemeProvider(mapTheme).then((provider) => {
        if (!secondaryViewerRef.current || secondaryViewerRef.current.isDestroyed()) return;
        secondaryViewerRef.current.imageryLayers.removeAll();
        secondaryViewerRef.current.imageryLayers.addImageryProvider(provider);
        secondaryViewerRef.current.scene.requestRender();
      });
    }

    const hide = (sel: string) => {
      const el2 = (sv.container as HTMLElement).querySelector(sel) as HTMLElement | null;
      if (el2) el2.style.display = 'none';
    };
    ['cesium-viewer-bottom', '.cesium-viewer-toolbar',
      '.cesium-viewer-animationContainer', '.cesium-viewer-timelineContainer',
      '.cesium-viewer-fullscreenContainer'].forEach(hide);

    sv.scene.morphTo2D(0);

    const secMissionDS = new Cesium.CustomDataSource('mission-secondary');
    sv.dataSources.add(secMissionDS);
    secondarySharedDataSourceRef.current = secMissionDS;

    const secHighlightDS = new Cesium.CustomDataSource('highlight-secondary');
    sv.dataSources.add(secHighlightDS);
    secondaryHighlightDataSourceRef.current = secHighlightDS;

    const secSelectionDS = new Cesium.CustomDataSource('selection-secondary');
    sv.dataSources.add(secSelectionDS);
    secondarySelectionDataSourceRef.current = secSelectionDS;

    const secPreviewDS = new Cesium.CustomDataSource('sensorPreview-secondary');
    sv.dataSources.add(secPreviewDS);
    secondaryPreviewDataSourceRef.current = secPreviewDS;

    const secMeasureDS = new Cesium.CustomDataSource('measure-secondary');
    sv.dataSources.add(secMeasureDS);
    secondaryMeasureDataSourceRef.current = secMeasureDS;

    const copyEntities = (primaryDS: any, secondaryDS: Cesium.CustomDataSource) => {
      if (!primaryDS) return;
      const realPrimary = primaryDS._primaryDS || primaryDS;
      realPrimary.entities.values.forEach((entity: any) => {
        try {
          secondaryDS.entities.add(new Cesium.Entity(entity));
        } catch (err) {
          console.error('Error copying entity to secondary viewer:', err);
        }
      });
    };

    copyEntities(sharedDataSourceRef.current, secMissionDS);
    copyEntities(highlightDataSourceRef.current, secHighlightDS);
    copyEntities(selectionDataSourceRef.current, secSelectionDS);
    copyEntities(previewDataSourceRef.current, secPreviewDS);
    copyEntities(measureDataSourceRef.current, secMeasureDS);

    const primary = viewerRef.current;
    if (primary) {
      sv.clock.currentTime = primary.clock.currentTime.clone();
      sv.clock.startTime = primary.clock.startTime.clone();
      sv.clock.stopTime = primary.clock.stopTime.clone();
      sv.clock.clockRange = primary.clock.clockRange;
      sv.clock.multiplier = primary.clock.multiplier;
      sv.clock.shouldAnimate = primary.clock.shouldAnimate;
      try {
        const cart = Cesium.Cartographic.fromCartesian(primary.camera.positionWC);
        if (cart) {
          sv.camera.setView({
            destination: Cesium.Cartesian3.fromRadians(cart.longitude, cart.latitude, Math.max(cart.height, 2_000_000)),
          });
        }
      } catch { /* ignore */ }
    }

    if (primary) {
      const removeClock = primary.clock.onTick.addEventListener((tick: Cesium.Clock) => {
        const sv2 = secondaryViewerRef.current;
        if (!sv2 || sv2.isDestroyed()) return;
        sv2.clock.currentTime = tick.currentTime.clone();
      });
      clockSyncRemoveRef.current = removeClock;

      const removeCamera = primary.camera.changed.addEventListener(() => {
        const sv2 = secondaryViewerRef.current;
        if (!sv2 || sv2.isDestroyed()) return;
        try {
          const cart = Cesium.Cartographic.fromCartesian(primary.camera.positionWC);
          if (cart) {
            sv2.camera.setView({
              destination: Cesium.Cartesian3.fromRadians(cart.longitude, cart.latitude, Math.max(cart.height, 2_000_000)),
            });
          }
        } catch { /* ignore */ }
      });
      cameraSyncRemoveRef.current = removeCamera;
    }

    secondaryViewerRef.current = sv;
    sv.scene.preRender.addEventListener(() => {
      (window as any).currentRenderingViewer = sv;
    });
    sv.useDefaultRenderLoop = true;
  }, [buildMapThemeProvider, mapTheme]);

  const destroySecondaryViewer = useCallback(() => {
    if (clockSyncRemoveRef.current) {
      try { clockSyncRemoveRef.current(); } catch { /* ignore */ }
      clockSyncRemoveRef.current = null;
    }
    if (cameraSyncRemoveRef.current) {
      try { cameraSyncRemoveRef.current(); } catch { /* ignore */ }
      cameraSyncRemoveRef.current = null;
    }
    const sv = secondaryViewerRef.current;
    if (sv && !sv.isDestroyed()) {
      try { sv.useDefaultRenderLoop = false; } catch { /* ignore */ }
    }
  }, []);

  return {
    initSecondaryViewer,
    destroySecondaryViewer,
  };
}
